// Profil sayfası - Kullanıcı profil bilgileri
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiEdit3 } from 'react-icons/fi';
import { authService } from '../../services/authService';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {
      user_fullname: user?.fullname || '',
      user_email: user?.email || '',
      user_gsm: user?.gsm || '',
      user_department: user?.department || ''
    }
  });

  // Profil güncelleme mutation'ı
  const updateProfileMutation = useMutation(
    (data) => authService.updateProfile(data),
    {
      onSuccess: (data) => {
        toast.success('Profil başarıyla güncellendi');
        queryClient.invalidateQueries('auth-profile');
        setIsEditing(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Profil güncellenemedi');
      }
    }
  );

  // Form gönderimi
  const onSubmit = async (data) => {
    try {
      await updateProfileMutation.mutateAsync(data);
    } catch (error) {
      console.error('Profil güncelleme hatası:', error);
    }
  };

  // Düzenleme iptal
  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Profil
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Kişisel bilgilerinizi görüntüleyin ve düzenleyin
          </p>
        </div>
        
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="btn-primary flex items-center"
        >
          <FiEdit3 className="mr-2" size={16} />
          {isEditing ? 'İptal' : 'Düzenle'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profil kartı */}
        <div className="lg:col-span-1">
          <div className="card p-6">
            <div className="text-center">
              {/* Avatar */}
              <div className="mx-auto h-24 w-24 bg-brand-primary rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-white">
                  {user?.fullname?.charAt(0)?.toUpperCase()}
                </span>
              </div>

              {/* Kullanıcı bilgileri */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {user?.fullname}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {user?.email}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {user?.department}
              </p>

              {/* Durum badge */}
              <div className="mt-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.userType === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                  user?.userType === 'manager' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                }`}>
                  {user?.userType === 'admin' ? 'Yönetici' :
                   user?.userType === 'manager' ? 'Müdür' : 'Temsilci'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Profil formu */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Profil Bilgileri
            </h3>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ad Soyad */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FiUser className="inline mr-2" size={16} />
                    Ad Soyad
                  </label>
                  <input
                    type="text"
                    {...register('user_fullname', { required: 'Ad soyad gerekli' })}
                    disabled={!isEditing}
                    className={`input-field ${!isEditing ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                  />
                  {errors.user_fullname && (
                    <p className="mt-1 text-sm text-red-600">{errors.user_fullname.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FiMail className="inline mr-2" size={16} />
                    Email
                  </label>
                  <input
                    type="email"
                    {...register('user_email', { 
                      required: 'Email gerekli',
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: 'Geçerli email adresi giriniz'
                      }
                    })}
                    disabled={!isEditing}
                    className={`input-field ${!isEditing ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                  />
                  {errors.user_email && (
                    <p className="mt-1 text-sm text-red-600">{errors.user_email.message}</p>
                  )}
                </div>

                {/* Telefon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FiPhone className="inline mr-2" size={16} />
                    Telefon
                  </label>
                  <input
                    type="tel"
                    {...register('user_gsm', { required: 'Telefon numarası gerekli' })}
                    disabled={!isEditing}
                    className={`input-field ${!isEditing ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                  />
                  {errors.user_gsm && (
                    <p className="mt-1 text-sm text-red-600">{errors.user_gsm.message}</p>
                  )}
                </div>

                {/* Departman */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FiMapPin className="inline mr-2" size={16} />
                    Departman
                  </label>
                  <input
                    type="text"
                    {...register('user_department', { required: 'Departman gerekli' })}
                    disabled={!isEditing}
                    className={`input-field ${!isEditing ? 'bg-gray-50 dark:bg-gray-700' : ''}`}
                  />
                  {errors.user_department && (
                    <p className="mt-1 text-sm text-red-600">{errors.user_department.message}</p>
                  )}
                </div>
              </div>

              {/* Form butonları */}
              {isEditing && (
                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                    disabled={updateProfileMutation.isLoading}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={updateProfileMutation.isLoading}
                  >
                    {updateProfileMutation.isLoading ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Hesap bilgileri */}
          <div className="card p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Hesap Bilgileri
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Kullanıcı Adı
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user?.username}
                  </p>
                </div>
                <span className="text-xs text-gray-400">Değiştirilemez</span>
              </div>

              <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Hesap Tipi
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user?.userType === 'admin' ? 'Yönetici' :
                     user?.userType === 'manager' ? 'Müdür' : 'Temsilci'}
                  </p>
                </div>
                <span className="text-xs text-gray-400">Sistem tarafından belirlenir</span>
              </div>

              <div className="flex items-center justify-between py-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Son Giriş
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user?.lastLogin ? new Date(user.lastLogin).toLocaleString('tr-TR') : 'Bilinmiyor'}
                  </p>
                </div>
                <FiCalendar className="text-gray-400" size={16} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
